/**
 * BLRRD Pointwise Filters — Levels
 *
 * Algorithm: input/output black/white + gamma remapping. Clean-room from
 * Adobe Photoshop's documented Levels formulas and standard photo-editing
 * math (Schlag 1994 — color spaces; Reinhard / Ward / Pattanaik / Debevec
 * "High Dynamic Range Imaging", Chapter 6 — tone reproduction primitives).
 * NO GIMP source consulted; the formula is the same one Photoshop ships
 * since v1.0 (1990, public formula since the original CS3 docs).
 *
 *   out = outBlack + ((in - inBlack) / (inWhite - inBlack))^(1/gamma)
 *                    * (outWhite - outBlack)
 *
 * Pipeline slot: PRE-KERNEL on scene-linear ACEScg Float32 RGBA. Caller
 * passes the post-ingest buffer (after the ingest matrix + WB) before
 * tone-equalizer / kernel run. Levels graduates from "filter" to "color
 * tool" — operates on linear data, NOT on the encoded display image.
 *
 * Per-channel mode applies the curve independently to R/G/B (e.g. fix
 * a colour cast). Composite mode applies the same curve to all three
 * channels.
 *
 * Identity short-circuit: defaults (inBlack=0, inWhite=1, gamma=1,
 * outBlack=0, outWhite=1) return the same Float32Array reference,
 * preserving the pixel-hash + worker-parity baselines byte-identical.
 */

const IDENTITY_EPS = 1e-7;
const RANGE_EPS    = 1e-6;

/** Default Levels params — identity transform. */
export function defaultLevelsParams() {
    return {
        inBlack:  0,
        inWhite:  1,
        gamma:    1,
        outBlack: 0,
        outWhite: 1,
        perChannel: false,
    };
}

/** True iff `params` represents the identity transform (no-op). */
export function isIdentityLevels(params) {
    if (!params) return true;
    const p = params;
    if (Math.abs((p.inBlack  ?? 0) - 0) > IDENTITY_EPS) return false;
    if (Math.abs((p.inWhite  ?? 1) - 1) > IDENTITY_EPS) return false;
    if (Math.abs((p.gamma    ?? 1) - 1) > IDENTITY_EPS) return false;
    if (Math.abs((p.outBlack ?? 0) - 0) > IDENTITY_EPS) return false;
    if (Math.abs((p.outWhite ?? 1) - 1) > IDENTITY_EPS) return false;
    /* perChannel toggle without changed channel curves is still identity. */
    if (p.perChannelParams) {
        for (const ch of ['r', 'g', 'b']) {
            if (p.perChannelParams[ch] && !isIdentityLevels(p.perChannelParams[ch])) {
                return false;
            }
        }
    }
    return true;
}

/**
 * Apply Levels to a Float32 RGBA buffer in place.
 *
 * @param {Float32Array} rgba       w*h*4 Float32, scene-linear
 * @param {number}       w
 * @param {number}       h
 * @param {object}       params     see defaultLevelsParams()
 * @param {object}       [opts]     { selectionMask?: Float32Array(w*h) | null }
 * @returns {Float32Array}          same `rgba` reference
 */
export function applyLevels(rgba, w, h, params, opts) {
    validateBuffer(rgba, w, h);
    if (isIdentityLevels(params)) return rgba;

    const mask = opts && opts.selectionMask ? opts.selectionMask : null;
    if (mask && mask.length !== w * h) {
        throw new Error('applyLevels: selectionMask length mismatch');
    }

    const channelParams = buildChannelParams(params);
    const N = w * h;
    for (let p = 0; p < N; p++) {
        const i = p << 2;
        const m = mask ? mask[p] : 1;
        if (m <= 0) continue;
        const newR = remap(rgba[i],     channelParams.r);
        const newG = remap(rgba[i + 1], channelParams.g);
        const newB = remap(rgba[i + 2], channelParams.b);
        if (m >= 1) {
            rgba[i]     = newR;
            rgba[i + 1] = newG;
            rgba[i + 2] = newB;
        } else {
            const inv = 1 - m;
            rgba[i]     = inv * rgba[i]     + m * newR;
            rgba[i + 1] = inv * rgba[i + 1] + m * newG;
            rgba[i + 2] = inv * rgba[i + 2] + m * newB;
        }
    }
    return rgba;
}

/* Single-pixel remap. Used per-channel. */
function remap(v, p) {
    const range = p.inWhite - p.inBlack;
    if (Math.abs(range) < RANGE_EPS) {
        /* User collapsed the range — return outBlack to avoid NaN/Inf. */
        return p.outBlack;
    }
    let norm = (v - p.inBlack) / range;
    /* Clamp to [0, 1] before pow — negatives + fractional gamma → NaN. */
    if (norm < 0) norm = 0;
    else if (norm > 1) norm = 1;
    if (Math.abs(p.gamma - 1) > IDENTITY_EPS) {
        norm = Math.pow(norm, 1 / p.gamma);
    }
    return p.outBlack + norm * (p.outWhite - p.outBlack);
}

/* Expand the composite or per-channel params into three concrete curves. */
function buildChannelParams(params) {
    const base = {
        inBlack:  params.inBlack  ?? 0,
        inWhite:  params.inWhite  ?? 1,
        gamma:    params.gamma    ?? 1,
        outBlack: params.outBlack ?? 0,
        outWhite: params.outWhite ?? 1,
    };
    if (!params.perChannel || !params.perChannelParams) {
        return { r: base, g: base, b: base };
    }
    const pc = params.perChannelParams;
    return {
        r: { ...base, ...(pc.r || {}) },
        g: { ...base, ...(pc.g || {}) },
        b: { ...base, ...(pc.b || {}) },
    };
}

function validateBuffer(rgba, w, h) {
    if (!(rgba instanceof Float32Array)) {
        throw new TypeError('applyLevels: rgba must be Float32Array');
    }
    if (rgba.length !== w * h * 4) {
        throw new Error('applyLevels: rgba.length !== w*h*4');
    }
}
